import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button } from "@postdaily/ui/components/button";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { WorkspaceAuditPanel } from "@/components/audit/workspace-audit-panel";
import { PageHeader } from "@/components/page-header";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { useUpgradeDialogStore } from "@/stores/upgrade-dialog-store";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/settings/audit")({
  component: RouteComponent,
});

function AuditPageHeader() {
  return (
    <PageHeader
      size="lg"
      title="Audit log"
      description="Review workspace changes, publishing events, and support-critical activity."
    />
  );
}

function RouteComponent() {
  const trpc = useTRPC();
  const { currentWorkspace } = useCurrentWorkspace();
  const openUpgradeDialog = useUpgradeDialogStore(
    (store) => store.openUpgradeDialog,
  );
  // The audit log is an Agency feature; the router enforces it, so this only
  // decides between the panel and the upsell.
  const billingState = useQuery(
    trpc.billing.state.queryOptions(
      { workspaceId: currentWorkspace?.id ?? "" },
      { enabled: Boolean(currentWorkspace?.id) },
    ),
  );

  if (!currentWorkspace) {
    return (
      <div className="flex flex-col gap-6">
        <AuditPageHeader />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const canViewAudit =
    currentWorkspace.role === "owner" || currentWorkspace.role === "admin";

  if (!canViewAudit) {
    return (
      <div className="flex flex-col gap-6">
        <AuditPageHeader />
        <Alert>
          <AlertTitle>Audit log unavailable</AlertTitle>
          <AlertDescription>
            Your role can view workspace content but cannot open the full audit
            log.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (billingState.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <AuditPageHeader />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (!billingState.data?.limits.auditLog) {
    return (
      <div className="flex flex-col gap-6">
        <AuditPageHeader />
        <Alert>
          <AlertTitle>Audit log is an Agency feature</AlertTitle>
          <AlertDescription>
            See every change made in this workspace — who connected a channel,
            edited a post, changed a role or touched billing — with a
            filterable, exportable history.
          </AlertDescription>
          <AlertAction>
            <Button
              onClick={() =>
                openUpgradeDialog({
                  feature: "audit.read",
                  highlightPlan: "agency",
                  reason:
                    "The workspace audit log is included on the Agency plan.",
                })
              }
              size="sm"
            >
              Upgrade to Agency
            </Button>
          </AlertAction>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <AuditPageHeader />
      <WorkspaceAuditPanel workspaceId={currentWorkspace.id} />
    </div>
  );
}
